import {useEffect, useRef} from 'react';
import {gsap} from 'gsap';
import SafeIcon from '../common/SafeIcon';
import './ChromaGrid.css';

export default function ChromaGrid({
  items = [],
  className = '',
  radius = 220,
  damping = 0.45,
  fadeOut = 0.5,
  ease = 'power3.out',
  onItemClick
}) {
  const rootRef = useRef(null);
  const overlayRef = useRef(null);
  const setX = useRef(null);
  const setY = useRef(null);
  const position = useRef({x: 0, y: 0});

  useEffect(() => {
    const element = rootRef.current;
    if (!element) return undefined;

    setX.current = gsap.quickSetter(element, '--x', 'px');
    setY.current = gsap.quickSetter(element, '--y', 'px');

    const {width, height} = element.getBoundingClientRect();
    position.current = {x: width / 2, y: height / 2};
    setX.current(position.current.x);
    setY.current(position.current.y);

    const currentPosition = position.current;
    const overlay = overlayRef.current;

    return () => {
      gsap.killTweensOf(currentPosition);
      if (overlay) gsap.killTweensOf(overlay);
    };
  }, []);

  const moveTo = (x, y) => {
    gsap.to(position.current, {
      x,
      y,
      duration: damping,
      ease,
      overwrite: true,
      onUpdate: () => {
        setX.current?.(position.current.x);
        setY.current?.(position.current.y);
      }
    });
  };

  const isMouseLike = (event) => event.pointerType !== 'touch';

  const showOverlay = () => {
    gsap.to(overlayRef.current, {opacity: 1, duration: 0.35, overwrite: true});
  };

  const hideOverlay = () => {
    gsap.to(overlayRef.current, {opacity: 0, duration: fadeOut, overwrite: true});
  };

  const handlePointerMove = (event) => {
    if (!isMouseLike(event)) return;
    const bounds = rootRef.current?.getBoundingClientRect();
    if (!bounds) return;
    moveTo(event.clientX - bounds.left, event.clientY - bounds.top);
    showOverlay();
  };

  const handleCardMove = (event) => {
    if (!isMouseLike(event)) return;
    const bounds = event.currentTarget.getBoundingClientRect();
    event.currentTarget.style.setProperty(
      '--mouse-x',
      `${event.clientX - bounds.left}px`
    );
    event.currentTarget.style.setProperty(
      '--mouse-y',
      `${event.clientY - bounds.top}px`
    );
  };

  return (
    <div
      ref={rootRef}
      className={`chroma-grid ${className}`}
      style={{'--r': `${radius}px`}}
      onPointerMove={handlePointerMove}
      onPointerLeave={hideOverlay}
    >
      {items.map((item) => (
        <a
          key={item.key || item.url}
          href={item.url}
          target={item.external ? '_blank' : undefined}
          rel={item.external ? 'noreferrer' : undefined}
          className="chroma-card"
          onPointerMove={handleCardMove}
          onClick={() => onItemClick?.(item)}
          style={{
            '--card-border': item.borderColor,
            '--card-gradient': item.gradient,
            '--card-accent': item.borderColor
          }}
        >
          <span className="chroma-card-icon">
            <SafeIcon icon={item.icon} />
          </span>
          <span className="chroma-info">
            <span className="chroma-kicker">{item.kicker}</span>
            <strong>{item.title}</strong>
            <small>{item.subtitle}</small>
            {item.badge && (
              <img
                className="chroma-card-badge"
                src={item.badge}
                alt="Buy Dadda Cool a coffee"
                loading="lazy"
              />
            )}
          </span>
          <span className="chroma-card-arrow">
            <SafeIcon icon={item.arrowIcon} />
          </span>
        </a>
      ))}
      <div ref={overlayRef} className="chroma-overlay" aria-hidden="true" />
    </div>
  );
}